import React, { useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { isNullish } from "utils/utils";
import { Stack } from "@mui/material";
import { searchErrorHandler } from "utils/formValidation";

import {
  createRole,
  getPermissionTagList,
  getRoleById,
  updateRole,
} from "./model";
import s from "./roleCreate.module.scss";

import Spin from "components/Spin/Spin";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Divider from "components/Divider/Divider";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import RolePermissionBlock from "./CommonComponent/RolePermissionBlock";
import { PortalContext } from "App";

import responseCodeMapping from "utils/response_code_mapping.json";
import rolesAndPermissions from "routes/rolesAndPermissions";
import RolePermissionBuildingBlock from "./CommonComponent/RolePermissionBuildingBlock";
import { useRoleOptionsCache } from "utils/optionsCache/roleOptionsCache";

const PAGE_LOCALE_KEY = "page.rolePermissions.roleCreate.";
const DIALOG_LOCALE_KEY = "page.dialogs.";

const ROLE_FIELD_INIT_STATE = {
  roleName: "",
  roleDescription: "",
  uiPermissionTags: [],
  apiPermissionTags: [],
};

const ERROR_INIT_STATE = {
  roleName: false,
  roleDescription: false,
  uiPermissionTags: [],
  apiPermissionTags: [],
};
const roleCreate = ({ path, redirectFn }) => {
  const [roleId, setRoleId] = useState(null);
  const [roleCreateFlag, setRoleCreateFlag] = useState(true);

  const { t } = useTranslation();
  const { logout } = useAuth();
  const { invalidateRoleOptionsCache } = useRoleOptionsCache();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "RolePermissionContentGrid";

  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;

  const [isLoadingRoleTags, setIsLoadingRoleTags] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(true);
  const detailLoading = isLoadingRoleTags || isLoadingDetail;

  const [roleTags, setRoleTags] = useState({});
  const [roleFieldInput, setRoleFieldInput] = useState(ROLE_FIELD_INIT_STATE);

  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);

  const [submitting, setSubmitting] = useState(false);
  const [selectAll, setSelectAll] = useState([]);
  const [rolePermissionTagsJson, setRolePermissionTagsJson] = useState({});
  const scrollToTopRef = useRef(null);

  useEffect(() => {
    // this is render the role (get the detail of the role)
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setRoleCreateFlag(detailPageState[path].createFlag);
      setRoleId(detailPageState[path].id);
    } else if (sessionState && sessionState[path]) {
      setRoleCreateFlag(sessionState[path].createFlag);
      setRoleId(sessionState[path].id);
    } else {
      setRoleCreateFlag(true);
      setRoleId("");
    }
  }, [detailPageState[path]]);

  useEffect(() => {
    (async () => {
      await initialFetchRoleTags();
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (roleId != null && Object.keys(rolePermissionTagsJson).length > 0) {
        if (roleCreateFlag) {
          setRoleFieldInput(ROLE_FIELD_INIT_STATE);
          redirectFn("update", { name: "Create Role", path });
          setIsLoadingDetail(false);
        } else {
          await initialFetchDetails(roleId);
        }
      }
    })();
  }, [rolePermissionTagsJson, roleId, roleCreateFlag]);

  useEffect(() => {
    checkIsSelectedAll();
  }, [roleFieldInput.apiPermissionTags, roleFieldInput.uiPermissionTags]);

  const initialFetchRoleTags = async () => {
    try {
      const apiResponse = await getPermissionTagList();
      // transaform the data for select All usage.
      const transformedData = {};
      Object.entries(apiResponse.data.data).forEach(([key, value]) => {
        transformedData[key] = [];
        value.mappingList.map((item) => {
          transformedData[key].push(item.uiPermission);
          if (!!item.apiPermissions) {
            transformedData[key].push(...item.apiPermissions);
          }
        });
      });
      setRolePermissionTagsJson(transformedData);

      if (!apiResponse || !apiResponse?.data) {
        snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
      } else {
        switch (apiResponse.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            const responseBody = apiResponse.data;
            if (responseBody.responseCode === "0000") {
              console.log("setting permission tags", responseBody.data);
              setRoleTags(responseBody.data);
            } else {
              console.log("cannot fetch role tags", "error");
              snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
            }
          }
        }
      }
    } catch (error) {
      console.log("error: ", error);
      snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
    } finally {
      setIsLoadingRoleTags(false);
    }
  };

  const initialFetchDetails = async (roleId) => {
    try {
      const apiResponse = await getRoleById(roleId);
      if (!apiResponse || !apiResponse?.data) {
        snackbar(t("general.message.networkError"), "error");
      } else {
        switch (apiResponse.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            const responseBody = apiResponse.data;
            if (responseBody.responseCode === "0000") {
              const parsedData = { ...ROLE_FIELD_INIT_STATE };
              parsedData.roleName = responseBody.data.role.name;
              parsedData.roleDescription = responseBody.data.role.description;
              parsedData.uiPermissionTags =
                responseBody.data.role.uiPermissionTags ?? [];
              parsedData.apiPermissionTags =
                responseBody.data.role.apiPermissionTags ?? [];
              console.log("setting role detail tags: ", parsedData);
              setRoleFieldInput(parsedData);
            } else {
              snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
            }
          }
        }
      }
    } catch (error) {
      console.log("error: ", error);
      snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // ===================== Detail field function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    setError({ ...error, [field]: false });
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setRoleFieldInput({ ...roleFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (roleFieldInput[field] && callback) {
      const isValidValue = callback(roleFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const isNotEmpty = (txt) => {
    if (isNullish(txt)) return false;
    if (txt == "") return false;
    return true;
  };

  const checkIsSelectedAll = () => {
    const selectAllTitleList = [
      // "Onboarding",
      "Merchant_Management",
      // "Account_Management",
      "Finance",
      // "Risk",
      "Tools",
      // "Analytics",
      // "Data",
      "Role_Permission",
      "Transactions",
      // "Terminal_Management",
    ];
    const tempSelectAll = JSON.parse(JSON.stringify(selectAll));
    selectAllTitleList.forEach((value) => {
      const allTheFieldTags = rolePermissionTagsJson[value];
      if (allTheFieldTags) {
        const recentTotalTags = [
          ...roleFieldInput.uiPermissionTags,
          ...roleFieldInput.apiPermissionTags,
        ];

        const isAllSelected = allTheFieldTags.every((tag) =>
          recentTotalTags.includes(tag),
        );

        const selectTag = `SELECT_ALL_${value}`;

        // Delete Tag Flow
        if (!isAllSelected) {
          // If not all tags are selected, we should remove the selectTag from tempSelectAll
          const index = tempSelectAll.indexOf(selectTag);
          if (index !== -1) {
            // If selectTag exists in tempSelectAll, remove it
            tempSelectAll.splice(index, 1);
          }
        } else {
          // If all tags are selected and the selectTag is not already in tempSelectAll
          if (!tempSelectAll.includes(selectTag)) {
            tempSelectAll.push(selectTag);
          }
        }
      }
    });
    setSelectAll(tempSelectAll);
  };

  const handleSingleCheckboxOncheck = (e, type, tag) => {
    console.log("handleSingleCheckboxOncheck", e, "type: ", type, "tag: ", tag);
    const updatedTags =
      type === "uiPermissionTags"
        ? [...roleFieldInput.uiPermissionTags]
        : [...roleFieldInput.apiPermissionTags];

    if (e) {
      // If e is true, add the tag
      if (!updatedTags.includes(tag)) {
        updatedTags.push(tag);
      }
    } else {
      // If e is false, remove the tag
      const index = updatedTags.indexOf(tag);
      if (index > -1) {
        updatedTags.splice(index, 1);
      }
    }

    // Update the state based on the type
    setRoleFieldInput({
      ...roleFieldInput,
      [type]: updatedTags,
    });
  };
  const handleSelectAllCheckboxOncheck = (e, type, tag) => {
    console.log(
      "handleSelectAllCheckboxOncheck",
      "e",
      e,
      "type",
      type,
      "tag",
      tag,
    );
    const apiTempList = [...roleFieldInput.apiPermissionTags];
    const uiTempList = [...roleFieldInput.uiPermissionTags];

    Object.entries(roleTags).forEach(([subTitle, value]) => {
      if (type === subTitle) {
        // If e is true, add tags
        if (e) {
          value.mappingList?.forEach((permission) => {
            if (!uiTempList.includes(permission.uiPermission)) {
              uiTempList.push(permission.uiPermission);
            }
            permission.apiPermissions?.forEach((apiPermission) => {
              if (!apiTempList.includes(apiPermission)) {
                apiTempList.push(apiPermission);
              }
            });
          });

          // Update state for adding tags
          setRoleFieldInput({
            ...roleFieldInput,
            uiPermissionTags: uiTempList,
            apiPermissionTags: apiTempList,
          });

          // Handle select all logic
          if (tag.includes("SELECT_ALL_")) {
            setSelectAll([...selectAll, tag]);
          }
        } else {
          // If e is false, remove tags
          value.mappingList?.forEach((permission) => {
            // Remove uiPermission from uiTempList
            const uiIndex = uiTempList.indexOf(permission.uiPermission);
            if (uiIndex > -1) {
              uiTempList.splice(uiIndex, 1);
            }

            // Remove associated apiPermissions from apiTempList
            permission.apiPermissions?.forEach((apiPermission) => {
              const apiIndex = apiTempList.indexOf(apiPermission);
              if (apiIndex > -1) {
                apiTempList.splice(apiIndex, 1);
              }
            });
          });

          // Update state for removing tags
          setRoleFieldInput({
            ...roleFieldInput,
            uiPermissionTags: uiTempList,
            apiPermissionTags: apiTempList,
          });

          // Optionally handle deselect all logic here if needed
          if (tag.includes("SELECT_ALL_")) {
            setSelectAll(
              selectAll.filter((selectedTag) => selectedTag !== tag),
            );
          }
        }
      }
    });
  };
  // ===================== detail field function end ===================== //

  // ===================== detail function  ===================== //

  const sendDetailUpdate = async () => {
    setSubmitting(true);
    const payload = { ...roleFieldInput };
    const { errorObj, hasError } = searchErrorHandler(roleFieldInput, [
      (input) =>
        input.roleName.trim() === "" && {
          roleName: true,
        },
    ]);
    setError(errorObj);
    if (hasError) {
      scrollToTop();
      setSubmitting(false);
      return;
    }
    if (roleId === "") {
      const response = await createRole(payload);

      if (!response || !response?.data) {
        snackbar(t(PAGE_LOCALE_KEY + "updateFail"), "error");
      } else {
        const responseBody = response.data;
        switch (response.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            if (responseBody.responseCode === "0000") {
              invalidateRoleOptionsCache();
              snackbar(t(PAGE_LOCALE_KEY + "msgSuccess"), "success");

              const path =
                "/roles-and-permissions/roleManagement/roleManagement";
              const payload = {
                name: rolesAndPermissions.find((tab) => tab.path === path)
                  .tabName,
                path: path,
                refresh: "refresh",
              };
              redirectFn("redirectPage", payload);
              setRoleFieldInput(ROLE_FIELD_INIT_STATE);
            } else {
              snackbar(
                t(PAGE_LOCALE_KEY + "updateFail") +
                  responseCodeMapping[responseBody.responseCode ?? "error"][
                    "en"
                  ],
                "error",
              );
            }
          }
        }
      }
    } else {
      const response = await updateRole(roleId, payload);
      if (!response || !response?.data) {
        snackbar(t(PAGE_LOCALE_KEY + "updateFail"), "error");
      } else {
        const responseBody = response.data;
        switch (response.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            if (responseBody.responseCode === "0000") {
              invalidateRoleOptionsCache();
              snackbar(t(PAGE_LOCALE_KEY + "msgSuccess"), "success");
              const path =
                "/roles-and-permissions/roleManagement/roleManagement";
              const payload = {
                name: rolesAndPermissions.find((tab) => tab.path === path)
                  .tabName,
                path: path,
                refresh: "refresh",
              };
              redirectFn("redirectPage", payload);
            } else {
              snackbar(
                t(PAGE_LOCALE_KEY + "updateFail") +
                  responseCodeMapping[responseBody.responseCode ?? "error"][
                    "en"
                  ],
                "error",
              );
            }
          }
        }
      }
    }

    setSubmitting(false);
  };
  const RoleDetailContent = () => {
    return (
      <div className={s.detailsContainer}>
        <div className={s.content2}>
          <div className={s.itemContainer}>
            <Grid gridId={gridId}>
              <OutlinedText
                label={t(PAGE_LOCALE_KEY + "columns.roleName.label")}
                value={roleFieldInput.roleName}
                onchangeFn={(e) => textInputOnChange(e, "roleName")}
                onfocusFn={() => textInputOnFocus("roleName")}
                onblurFn={() => textInputOnBlur("roleName", isNotEmpty)}
                sxParams={{
                  textFocus: focus.roleName,
                  textError: error.roleName,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "columns.roleName.errorInput",
                )}
              />
              <OutlinedText
                label={t(PAGE_LOCALE_KEY + "columns.roleDescription.label")}
                value={roleFieldInput.roleDescription}
                onchangeFn={(e) => textInputOnChange(e, "roleDescription")}
                onfocusFn={() => textInputOnFocus("roleDescription")}
                onblurFn={() => textInputOnBlur("roleDescription", isNotEmpty)}
                sxParams={{
                  textFocus: focus.roleDescription,
                  textError: error.roleDescription,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "columns.roleDescription.errorInput",
                )}
              />
            </Grid>
          </div>
        </div>
        <div className={s.header}>
          <span>{t(PAGE_LOCALE_KEY + "columns.permission.label")}</span>
        </div>{" "}
        <Divider />
        {RoleTagSection()}
        <div className={s.dialogActionContainer}>
          <div></div>
          <div className={s.dialogButtonContainer}>
            <ButtonMAS overlayLoading
              disabled={submitting || detailLoading}
              loading={submitting}
              onClick={sendDetailUpdate}
            >
              {roleCreateFlag
                ? t(PAGE_LOCALE_KEY + "btnCreate")
                : t(DIALOG_LOCALE_KEY + "btnUpdate")}
            </ButtonMAS>
          </div>
        </div>
      </div>
    );
  };

  const RoleTagSection = () => {
    if (Object.entries(roleTags).length === 0) {
      return (
        <div className={s.content1}>
          <Stack direction="row" justifyContent="center">
            <span>{t(PAGE_LOCALE_KEY + "noRecord")}</span>
          </Stack>
        </div>
      );
    } else {
      return <div>{RoleTagContent()}</div>;
    }
  };

  const RoleTagContent = () => {
    const entries = Object.entries(roleTags);
    const sortedEntries = entries.sort(
      ([, a], [, b]) => a.priority - b.priority,
    );
    return (
      <div className={s.content1}>
        {sortedEntries.map(([key, value], index) => {
          return (
            <div key={index} className={s.itemContainer}>
              <div className={s.permissionContainer}>
                <span style={{ fontSize: "20px" }}>
                  {" "}
                  {key.replace("_", " ")}{" "}
                </span>
                <RolePermissionBuildingBlock
                  title={"Select All"}
                  tag={"SELECT_ALL_" + key}
                  type={key}
                  callBack={handleSelectAllCheckboxOncheck}
                  selectAllList={selectAll}
                  permissionTagList={[
                    ...roleFieldInput.apiPermissionTags,
                    ...roleFieldInput.uiPermissionTags,
                  ]}
                />
              </div>
              {value.mappingList.map((item, index) => {
                return (
                  <div className={s.gridContaner} key={index}>
                    <RolePermissionBlock
                      permission={item}
                      callBack={handleSingleCheckboxOncheck}
                      permissionTagList={[
                        ...roleFieldInput.apiPermissionTags,
                        ...roleFieldInput.uiPermissionTags,
                      ]}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  // ===================== detail function end ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const isFetchedPermissionTagsFail = () => {
    return !roleTags || Object.entries(roleTags).length === 0;
  };

  const renderRoleDetail = () => {
    if (submitting) {
      return (
        <div className={s.detailPage}>
          <Spin type={"search"} />
          <Divider />
          {RoleDetailContent()}
        </div>
      );
    }

    if (isFetchedPermissionTagsFail()) {
      return (
        <div className={s.detailPage}>
          <div className={s.content1}>
            <Stack direction="row" justifyContent="center">
              <span>{t(PAGE_LOCALE_KEY + "noRecord")}</span>
            </Stack>
          </div>
        </div>
      );
    }

    const headerTitle = roleCreateFlag
      ? t(PAGE_LOCALE_KEY + "sectionCreateTitle")
      : `${t(PAGE_LOCALE_KEY + "sectionEditTitle")}: ${roleId}`;

    return (
      <div className={s.detailPage}>
        <div className={s.header}>
          <span>{headerTitle}</span>
        </div>
        <Divider />
        {RoleDetailContent()}
      </div>
    );
  };

  const scrollToTop = () => {
    if (scrollToTopRef.current) {
      scrollToTopRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className={s.container} ref={scrollToTopRef}>
      <div className={s.contentContainer}>
        {detailLoading ? (
          <div className={s.spinloading}>
            <Spin />
          </div>
        ) : (
          renderRoleDetail()
        )}
      </div>
    </div>
  );
};

export default React.memo(roleCreate);
